import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { allUsers, getRoleBadgeColor } from "@/mocks/auth";

export default function Login() {
  const { t } = useTranslation();
  const { login, quickLogin } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showQuickSelect, setShowQuickSelect] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError(t("auth_invalid_credentials"));
      return;
    }
    setLoading(true);
    try {
      const ok = await login(email, password);
      if (ok) {
        navigate("/dashboard");
      } else {
        setError(t("auth_invalid_credentials"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = (userEmail: string) => {
    setLoading(true);
    setTimeout(() => {
      const ok = quickLogin(userEmail);
      if (ok) {
        navigate("/dashboard");
      }
      setLoading(false);
    }, 400);
  };

  const handleForgotSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true);
    setTimeout(() => {
      setForgotSent(true);
      setForgotLoading(false);
    }, 1200);
  };

  const byRole = {
    "Government Health Officer": allUsers.filter((u) => u.role === "Government Health Officer"),
    "Organization User": allUsers.filter((u) => u.role === "Organization User"),
    Admin: allUsers.filter((u) => u.role === "Admin"),
    Viewer: allUsers.filter((u) => u.role === "Viewer"),
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-teal/10 rounded-full blur-[160px] animate-pulse-slow" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-amber/5 rounded-full blur-[140px] animate-pulse-slow" style={{ animationDelay: "2s" }} />
      </div>

      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 flex items-center justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/0a8677a0-78d8-4971-947c-fe12fe0ee52e.png"
              alt="MalariaGuard"
              className="w-10 h-10 object-contain rounded"
            />
          </div>
          <div>
            <h1 className="font-display font-bold text-lg tracking-tight text-slate-900 dark:text-white">MalariaGuard</h1>
            <p className="text-[10px] text-slate-400 font-mono tracking-wider uppercase">EpiSense AI</p>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 backdrop-blur-sm p-6 md:p-8 shadow-xl dark:shadow-2xl dark:shadow-black/20">
          <h2 className="text-lg font-display font-semibold text-slate-900 dark:text-white mb-1">{t("auth_welcome_back")}</h2>
          <p className="text-xs text-slate-500 dark:text-slate-500 mb-6">{t("auth_signin_desc")}</p>

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-xs text-red-400 flex items-center gap-2 animate-slide-up">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-sm" />
              </div>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_email")}</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 dark:text-slate-500">
                  <i className="ri-mail-line text-sm" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 pl-9 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-all hover:border-slate-400 dark:hover:border-slate-600"
                  placeholder="your@organization.org"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">{t("auth_password")}</label>
                <button
                  type="button"
                  onClick={() => { setShowForgotModal(true); setForgotSent(false); setForgotEmail(""); }}
                  className="text-[10px] text-teal hover:text-teal-hover transition-colors"
                >
                  {t("auth_forgot_password")}
                </button>
              </div>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 dark:text-slate-500">
                  <i className="ri-lock-line text-sm" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 pl-9 pr-10 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-all hover:border-slate-400 dark:hover:border-slate-600"
                  placeholder="Enter password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  tabIndex={-1}
                >
                  <i className={showPassword ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                </button>
              </div>
              <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1.5">
                {t("auth_password_hint")} <span className="font-mono text-slate-500 dark:text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-1 rounded">demo</span>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 text-teal focus:ring-teal/30 focus:ring-1 accent-teal cursor-pointer"
              />
              <label htmlFor="remember" className="text-xs text-slate-500 dark:text-slate-500 cursor-pointer select-none">
                {t("auth_remember_me")}
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-teal text-slate-950 text-sm font-semibold py-2.5 hover:bg-teal-hover transition-colors whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line text-sm animate-spin" />
                  </div>
                  {t("auth_signing_in")}
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-login-box-line text-sm" />
                  </div>
                  {t("auth_sign_in")}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setShowQuickSelect(!showQuickSelect)}
              className="flex items-center justify-between w-full text-xs text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-flashlight-line text-sm text-amber" />
                </div>
                {t("auth_quick_login")}
              </span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={showQuickSelect ? "ri-arrow-up-s-line text-sm" : "ri-arrow-down-s-line text-sm"} />
              </div>
            </button>

            {showQuickSelect && (
              <div className="mt-3 space-y-3 animate-fade-in">
                {Object.entries(byRole).map(([role, users]) => (
                  <div key={role}>
                    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 dark:text-slate-600 mb-1.5">{role}</p>
                    <div className="grid grid-cols-1 gap-1.5">
                      {users.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => handleQuickLogin(u.email)}
                          disabled={loading}
                          className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 px-3 py-2 text-left hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/40 transition-all group disabled:opacity-50"
                        >
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-bold flex-shrink-0 group-hover:bg-teal-muted/20 group-hover:text-teal transition-colors">
                            {u.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-slate-800 dark:text-white truncate">{u.name}</p>
                            <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">{u.email}</p>
                          </div>
                          <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded border font-medium flex-shrink-0 ${getRoleBadgeColor(u.role)}`}>
                            {u.role.split(" ")[0]}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <Link to="/" className="text-xs text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors flex items-center gap-1">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xs" />
              </div>
              {t("auth_back_home")}
            </Link>
            <Link to="/signup" className="text-xs text-teal hover:text-teal-hover transition-colors font-medium">
              {t("auth_no_account")}
            </Link>
          </div>
        </div>
      </div>

      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowForgotModal(false)} />
          <div className="relative w-full max-w-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl animate-slide-up">
            {!forgotSent ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t("auth_reset_password")}</h3>
                  <button
                    onClick={() => setShowForgotModal(false)}
                    className="w-6 h-6 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </p>
                <form onSubmit={handleForgotSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_email")}</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400 dark:text-slate-500">
                        <i className="ri-mail-line text-sm" />
                      </div>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950/60 px-3 py-2.5 pl-9 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal/30 transition-all"
                        placeholder="your@organization.org"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full rounded-lg bg-teal text-slate-950 text-sm font-semibold py-2.5 hover:bg-teal-hover transition-colors whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {forgotLoading ? (
                      <>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-loader-4-line text-sm animate-spin" />
                        </div>
                        {t("auth_sending")}
                      </>
                    ) : (
                      <>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-send-plane-line text-sm" />
                        </div>
                        {t("auth_send_reset")}
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-muted/20 mx-auto mb-4">
                  <i className="ri-mail-check-line text-teal text-xl" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">{t("auth_check_email")}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  {t("auth_reset_sent", { email: forgotEmail })}
                </p>
                <button
                  onClick={() => { setShowForgotModal(false); setForgotSent(false); setForgotEmail(""); }}
                  className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  {t("auth_back_to_signin")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}